/*
 * Screen-reader announcements for the AJ Zipcode Validator widget (theme-only workaround).
 *
 * The widget reveals its result by toggling display:none -> block on server-rendered divs.
 * role="status" regions don't announce that (success was silent) and role="alert" only
 * re-announces because it is hidden/shown each time. Nothing is announced when the same
 * result repeats. Instead of touching the app, we mirror the visible result into one
 * persistent live region that lives outside the widget's markup, and re-announce it after
 * every check the user starts. The widget's own role/aria-live attributes are removed so
 * results are not announced twice.
 *
 * Rules that keep the announcement predictable:
 *  - Only announce after the user pressed Check (or Enter). The widget also re-checks the
 *    saved pincode on page load; that result must stay silent.
 *  - Announce once, after the request has finished and the widget has updated the DOM, so a
 *    previous (stale) result is never read.
 *  - Read only text that is actually visible. The widget leaves hidden text inside the
 *    visible error box (e.g. "Sorry! Service is not available" next to a validation error).
 */
(function () {
  var MESSAGE_SELECTOR =
    '.ZipCodeRestrict .validation_msg .success_msg, ' +
    '.ZipCodeRestrict .validation_msg .error_msg, ' +
    '.ZipCodeRestrict .validation_msg .not_valid_error_msg, ' +
    '.ZipCodeRestrict .empty_val_error';
  var CHECK_URL = /\/appjetty-zipcode(?:[?#]|$)/;
  var CLEAR_DELAY = 150; // gap between clearing the region and adding the new text
  var SETTLE_DELAY = 300; // time for the widget to finish updating after the response
  var NO_REQUEST_DELAY = 500; // wait for a request before treating the check as client-side only

  var announcer = null;
  var announceTimer = null;
  var settleTimer = null;
  var pendingCheck = false; // a user-initiated check is waiting to be announced
  var inflight = 0; // check requests currently running
  var stripScheduled = false;
  var started = false;

  function getAnnouncer() {
    if (announcer) return announcer;
    announcer = document.getElementById('zip-sr-announcer');
    if (announcer) return announcer; // static region rendered by theme.liquid
    announcer = document.createElement('div');
    announcer.id = 'zip-sr-announcer';
    announcer.setAttribute('role', 'alert');
    announcer.setAttribute('aria-live', 'assertive');
    announcer.setAttribute('aria-atomic', 'true');
    announcer.style.cssText =
      'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;' +
      'clip:rect(0,0,0,0);white-space:nowrap;border:0;';
    document.body.appendChild(announcer);
    return announcer;
  }

  function isVisible(el) {
    return el.getClientRects().length > 0 && window.getComputedStyle(el).visibility !== 'hidden';
  }

  // Text of a node, skipping any descendant that is not rendered.
  function visibleText(node) {
    var out = '';
    for (var child = node.firstChild; child; child = child.nextSibling) {
      if (child.nodeType === 3) {
        out += child.nodeValue + ' ';
      } else if (child.nodeType === 1 && isVisible(child)) {
        out += visibleText(child) + ' ';
      }
    }
    return out;
  }

  function visibleMessage() {
    var parts = [];
    var nodes = document.querySelectorAll(MESSAGE_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      if (!isVisible(nodes[i])) continue;
      var text = visibleText(nodes[i]).replace(/\s+/g, ' ').trim();
      if (text && parts.indexOf(text) === -1) parts.push(text);
    }
    return parts.join('. ');
  }

  // Empty the region, then add the text as a brand-new element after a short delay. A new node
  // (rather than reused text) makes NVDA, JAWS and Narrator treat it as a fresh announcement,
  // so identical consecutive results are still read.
  function announce(text) {
    if (!text) return;
    var region = getAnnouncer();
    clearTimeout(announceTimer);
    region.textContent = '';
    announceTimer = setTimeout(function () {
      var line = document.createElement('p');
      line.style.margin = '0';
      line.textContent = text;
      region.appendChild(line);
    }, CLEAR_DELAY);
  }

  // Announce the final state of the pending check, once, when nothing is still running.
  function flush() {
    if (!pendingCheck || inflight > 0) return;
    pendingCheck = false;
    announce(visibleMessage());
  }

  function scheduleFlush(delay) {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(flush, delay);
  }

  function stripWidgetLiveAttributes() {
    stripScheduled = false;
    var nodes = document.querySelectorAll(MESSAGE_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].removeAttribute('role');
      nodes[i].removeAttribute('aria-live');
      nodes[i].removeAttribute('aria-atomic');
    }
  }

  function scheduleStrip() {
    if (stripScheduled) return;
    stripScheduled = true;
    window.requestAnimationFrame(stripWidgetLiveAttributes);
  }

  function beginCheck() {
    pendingCheck = true;
    // Drop the previous result so a stale message is never read while the new check runs.
    clearTimeout(announceTimer);
    getAnnouncer().textContent = '';
    // Client-side validation (empty / special characters) shows a message without a request.
    // If no request is running after this delay the message is already final.
    scheduleFlush(NO_REQUEST_DELAY);
  }

  // Count check requests without depending on which jQuery (or fetch) the widget uses.
  function trackRequests() {
    var xhrOpen = XMLHttpRequest.prototype.open;
    var xhrSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._zipCheck = CHECK_URL.test(String(url || ''));
      return xhrOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this._zipCheck) {
        inflight++;
        this.addEventListener('loadend', function () {
          inflight = Math.max(0, inflight - 1);
          if (pendingCheck) scheduleFlush(SETTLE_DELAY);
        });
      }
      return xhrSend.apply(this, arguments);
    };

    if (window.fetch) {
      var nativeFetch = window.fetch;
      window.fetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (!CHECK_URL.test(url)) return nativeFetch.apply(this, arguments);
        inflight++;
        var done = function () {
          inflight = Math.max(0, inflight - 1);
          if (pendingCheck) scheduleFlush(SETTLE_DELAY);
        };
        var p = nativeFetch.apply(this, arguments);
        p.then(done, done);
        return p;
      };
    }
  }

  function init() {
    getAnnouncer();
    stripWidgetLiveAttributes();
    trackRequests();

    // The widget may inject/re-render its markup after load, so strip again when it does.
    // While a check is pending, any change to the widget also restarts the settle timer so we
    // read the final state rather than an intermediate one.
    new MutationObserver(function () {
      scheduleStrip();
      if (pendingCheck && inflight === 0) scheduleFlush(SETTLE_DELAY);
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
      characterData: true
    });

    // Capture phase on native events: runs even if the widget stops propagation, and does not
    // depend on jQuery. Covers the Check button, Enter in the field, and form submission.
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.ZipCodeRestrict .zipcode_submit')) beginCheck();
    }, true);
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || !e.target.closest) return;
      if (e.target.closest('.ZipCodeRestrict') && e.target.tagName === 'INPUT') beginCheck();
    }, true);
    document.addEventListener('submit', function (e) {
      if (e.target.querySelector && e.target.querySelector('.ZipCodeRestrict')) beginCheck();
    }, true);
  }

  function start() {
    if (started) return;
    started = true;
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
